import { useRef, useCallback, useEffect } from "react";

// Generate a simple ringtone using Web Audio API
const createRingtone = (audioContext: AudioContext): OscillatorNode[] => {
  const oscillators: OscillatorNode[] = [];
  
  // Create a pleasant two-tone ringtone pattern
  const frequencies = [523.25, 659.25]; // C5 and E5
  
  frequencies.forEach((freq, i) => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
    
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillators.push(oscillator);
  });
  
  return oscillators;
};

export const useRingtone = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const vibrationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPlayingRef = useRef(false);

  const playRingPattern = useCallback(() => {
    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
      audioContextRef.current = new AudioContext();
    }

    const ctx = audioContextRef.current;
    
    // Play ring pattern: two short tones
    const playTone = (frequency: number, startTime: number, duration: number) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, startTime);
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05);
      gainNode.gain.setValueAtTime(0.3, startTime + duration - 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    const now = ctx.currentTime;
    
    // Ring pattern: two ascending tones
    playTone(523.25, now, 0.15); // C5
    playTone(659.25, now + 0.15, 0.15); // E5
    playTone(783.99, now + 0.3, 0.2); // G5
  }, []);

  const vibratePattern = useCallback(() => {
    // Check if Vibration API is supported
    if ("vibrate" in navigator) {
      // Vibration pattern: vibrate 200ms, pause 100ms, vibrate 200ms
      navigator.vibrate([200, 100, 200]);
    }
  }, []);

  const startRingtone = useCallback(() => {
    if (isPlayingRef.current) return;
    
    isPlayingRef.current = true;
    
    // Play audio immediately
    playRingPattern();
    
    // Vibrate immediately
    vibratePattern();
    
    // Then repeat every 2 seconds
    intervalRef.current = setInterval(() => {
      if (isPlayingRef.current) {
        playRingPattern();
      }
    }, 2000);

    // Vibration interval (slightly offset for natural feel)
    vibrationIntervalRef.current = setInterval(() => {
      if (isPlayingRef.current) {
        vibratePattern();
      }
    }, 2000);
  }, [playRingPattern, vibratePattern]);

  const stopRingtone = useCallback(() => {
    isPlayingRef.current = false;
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (vibrationIntervalRef.current) {
      clearInterval(vibrationIntervalRef.current);
      vibrationIntervalRef.current = null;
    }

    // Stop any ongoing vibration
    if ("vibrate" in navigator) {
      navigator.vibrate(0);
    }
    
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRingtone();
    };
  }, [stopRingtone]);

  return { startRingtone, stopRingtone };
};
